/**
 * What a video project counts, in words: the subject a person typed or the assistant suggested ("reindeer", "car",
 * "car, person"), else "object". One place for the page's texts, so "Cars counted", "Each car counts once" and
 * "Click a car or person that has no dot" all follow the subject, one thing or several.
 */

export const GENERIC = 'object';

// same singular and plural, and irregular plurals, for what drone surveys count
const SAME = new Set([
	'reindeer',
	'deer',
	'sheep',
	'moose',
	'elk',
	'bison',
	'fish',
	'salmon',
	'trout',
	'cattle',
	'swine',
	'aircraft',
	'livestock',
	'wildlife',
	'equipment',
]);
const IRREGULAR = {
	person: 'people',
	man: 'men',
	woman: 'women',
	child: 'children',
	mouse: 'mice',
	goose: 'geese',
	ox: 'oxen',
	calf: 'calves',
	wolf: 'wolves',
	leaf: 'leaves',
	knife: 'knives',
	half: 'halves',
	foot: 'feet',
	tooth: 'teeth',
};
const SINGULAR = Object.fromEntries(Object.entries(IRREGULAR).map(([s, p]) => [p, s]));

// the last word decides ("red car" → "red cars"); returns [head, last]
const split = phrase => {
	const i = phrase.lastIndexOf(' ');
	return i < 0 ? ['', phrase] : [phrase.slice(0, i + 1), phrase.slice(i + 1)];
};

/** "car" → "car", "cars" → "car", "people" → "person", "boxes" → "box"; a word with no plural stays. */
export const singular = phrase => {
	const [head, w] = split(phrase);
	const low = w.toLowerCase();
	if (SAME.has(low) || IRREGULAR[low]) return phrase;
	if (SINGULAR[low]) return head + SINGULAR[low];
	if (/[^aeiou]ies$/.test(low)) return head + w.slice(0, -3) + 'y';
	if (/(s|x|z|ch|sh)es$/.test(low)) return head + w.slice(0, -2);
	if (/[^s]s$/.test(low) && !/(us|is)$/.test(low)) return head + w.slice(0, -1);
	return phrase;
};

/** "car" → "cars", "person" → "people", "bus" → "buses", "reindeer" → "reindeer". */
export const plural = phrase => {
	const one = singular(phrase);
	const [head, w] = split(one);
	const low = w.toLowerCase();
	if (SAME.has(low)) return one;
	if (IRREGULAR[low]) return head + IRREGULAR[low];
	if (/[^aeiou]y$/.test(low)) return head + w.slice(0, -1) + 'ies';
	if (/(s|x|z|ch|sh)$/.test(low)) return head + w + 'es';
	return head + w + 's';
};

/** The things counted: "car, person" → ["car", "person"]; nothing → ["object"]. */
export const subjectsOf = text => {
	const names = String(text || '')
		.split(/[,;/]|\s+and\s+|\s*&\s*/i)
		.map(s => s.trim().toLowerCase())
		.filter(Boolean)
		.map(singular);
	return names.length ? [...new Set(names)] : [GENERIC];
};

const join = (words, word) =>
	words.length <= 1
		? words[0] || ''
		: `${words.slice(0, -1).join(', ')} ${word} ${words[words.length - 1]}`;

/** One of them, for "each …" / "a …": "car", "car or person". */
export const anyOne = text => join(subjectsOf(text), 'or');

/** All of them, for "… counted": "cars", "cars and people". */
export const allOf = text => join(subjectsOf(text).map(plural), 'and');

const cap = s => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** The count panel's title: "Car count", "Car and person count". */
export const countTitle = text => `${cap(join(subjectsOf(text), 'and'))} count`;

/** "Cars counted", "Cars and people counted". */
export const countedTitle = text => `${cap(allOf(text))} counted`;

/** "a car", "an animal", "a car or person". */
export const withArticle = phrase => `${/^[aeiou]/i.test(phrase) ? 'an' : 'a'} ${phrase}`;

/** Whether the subject is our reindeer (calves are told apart for reindeer only). */
export const isReindeer = text => {
	const s = subjectsOf(text);
	return s.length === 1 && s[0] === 'reindeer';
};
